import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { AgenteIaJobStatus, AgenteIaTipo } from '@/types/agentesIa';

interface EnqueueResponse {
  success: boolean;
  data: { jobId: string; status: string };
}

interface JobStatusResponse<TResult> {
  success: boolean;
  data: AgenteIaJobStatus<TResult>;
}

export function useAgenteIaJob<TPayload = unknown, TResult = unknown>(
  agente: AgenteIaTipo,
  empresaId: number | undefined
) {
  const [jobId, setJobId] = useState<string | null>(null);

  const enqueueMutation = useMutation({
    mutationFn: async (payload: TPayload) => {
      if (!empresaId) throw new Error('empresaId é obrigatório');
      const response = await api.post<EnqueueResponse>(`/empresas/${empresaId}/${agente}`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      setJobId(data.jobId);
    },
  });

  const jobStatusQuery = useQuery<AgenteIaJobStatus<TResult>>({
    queryKey: ['agente-ia-job', empresaId, jobId],
    queryFn: async () => {
      const response = await api.get<JobStatusResponse<TResult>>(
        `/empresas/${empresaId}/agentes-ia/jobs/${jobId}`
      );
      return response.data;
    },
    enabled: Boolean(empresaId && jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });

  const reset = useCallback(() => {
    setJobId(null);
  }, []);

  return {
    enqueue: enqueueMutation.mutateAsync,
    isEnqueuing: enqueueMutation.isPending,
    enqueueError: enqueueMutation.error,
    jobId,
    jobStatus: jobStatusQuery.data,
    isJobLoading: jobStatusQuery.isLoading,
    reset,
  };
}
